import React, { ReactNode, useEffect, useState } from 'react';
import { ILearningCenterArticle } from '../../../../../types/contentful';
import { Column, Layout } from '../../../Layout';
import { ArticleSnippet } from '../ArticleSnippet';
import { ListPreloader, ListWrapper, LoadMoreButton } from './styles';
import { ImageType } from '../ArticleSnippet/types';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { EmptyList } from './EmptyList';
import cx from 'classnames';

interface ArticleListProps {
  articles: ILearningCenterArticle[];
  lastPage: boolean;
  onLoadMore: (pageNumber: number, pageSize: number) => void;
}

type LoadingState = 'pending' | 'loading' | 'done';

export const ARTICLES_PER_PAGE = 7;
const COLUMNS_COUNT = 3;

function getItemProps(index: number, isDesktop: boolean) {
  // For an overview, refer to:
  // https://github.com/GreenlightMe/greenlight-website-next/pull/447#issue-1821247382

  // The next variables are used to define whether we need to show the image for
  // the current item. For that, we calculate the current block and the
  // current page, so we know the item index that will display the image.
  const PAGE_OFFSET = ARTICLES_PER_PAGE + 1;
  const blockSize = ARTICLES_PER_PAGE * COLUMNS_COUNT + 1;
  const itemIndex = index + 1;
  const prevBlockIndex = Math.floor(itemIndex / blockSize);
  const blockIndex = Math.floor((itemIndex + prevBlockIndex) / blockSize);
  const blockRemainder = itemIndex % blockSize;
  const pagePadding = blockIndex % blockSize;
  const blockItemIndex = (blockRemainder + pagePadding) % blockSize;
  const pageIndex = blockItemIndex < 8 ? 0 : blockItemIndex < 15 ? 1 : 2;
  const firstBlockPageTargetIndex = pageIndex * PAGE_OFFSET + 1;
  const otherBlocksPageTargetIndex =
    blockSize * blockIndex -
    Math.max(0, blockIndex - 1) +
    pageIndex * PAGE_OFFSET;
  const pageTargetIndex =
    blockIndex === 0 ? firstBlockPageTargetIndex : otherBlocksPageTargetIndex;
  const showImage = itemIndex === pageTargetIndex;

  // If not on desktop, the order doesn't matter but we still need to know when
  // to show the image. And if that's the case, we show the "small" variant.
  if (!isDesktop) {
    return {
      imageType: showImage ? 'small' : 'none'
    };
  }

  // But if on desktop, we calculate on which column to show the image, as well
  // as the correct order for the current item.
  const placeOnLeft = pageIndex === 0;
  const placeOnMiddle = pageIndex === 1;
  const isEvenBlock = blockIndex % 2 === 0;
  const isEvenItem = itemIndex % 2 === 0;
  let order = 0;

  // show image on current item
  if (showImage) {
    order = placeOnLeft ? 1 : placeOnMiddle ? 2 : 3;
  }

  // items of page with image on left
  else if (placeOnLeft && isEvenBlock) {
    order = isEvenItem ? 2 : 3;
  } else if (placeOnLeft) {
    order = isEvenItem ? 3 : 2;
  }

  // items of page with image on middle
  else if (placeOnMiddle && isEvenBlock) {
    order =
      itemIndex < pageTargetIndex ? (isEvenItem ? 1 : 3) : isEvenItem ? 3 : 1;
  } else if (placeOnMiddle) {
    order =
      itemIndex < pageTargetIndex ? (isEvenItem ? 3 : 1) : isEvenItem ? 1 : 3;
  }

  // items of page with image on right
  else if (isEvenBlock) {
    order =
      itemIndex < pageTargetIndex ? (isEvenItem ? 2 : 1) : isEvenItem ? 1 : 2;
  } else {
    order =
      itemIndex < pageTargetIndex ? (isEvenItem ? 1 : 2) : isEvenItem ? 2 : 1;
  }

  return {
    imageType: showImage ? 'large' : 'none',
    order
  };
}

export function ArticleSnippetList({
  articles,
  lastPage,
  onLoadMore
}: ArticleListProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingState, setLoadingState] = useState<LoadingState>('pending');
  const [articlesNodesList, setArticlesNodesList] = useState<ReactNode[]>([]);
  const isDesktop = useMediaQuery('desktop');

  useEffect(() => {
    const newArticlesNodes = articles.map<ReactNode>((article, index) => {
      const itemIndex = index + articlesNodesList.length;
      const { imageType } = getItemProps(itemIndex, isDesktop);

      return (
        <div
          className={cx(
            'column w-full desktop:justify-center [&>a>div]:h-full [&>a]:h-full',
            imageType === 'large'
              ? 'col-span-1 row-span-3'
              : 'col-span-1 row-span-1'
          )}
          key={article.sys.id}
          data-testid="article-snippet-list-item"
        >
          <ArticleSnippet
            fields={article.fields}
            listMode={true}
            image={imageType as ImageType}
          />
        </div>
      );
    });

    setArticlesNodesList([...articlesNodesList, ...newArticlesNodes]);
  }, [articles]);

  useEffect(() => {
    if (loadingState === 'loading') {
      onLoadMore(currentPage + 1, ARTICLES_PER_PAGE);
    }
  }, [loadingState]);

  useEffect(() => {
    if (loadingState === 'pending') {
      return;
    }

    const isLastPage = articles.length === 0;
    setLoadingState('done');

    if (isLastPage) {
      return;
    }

    setCurrentPage(currentPage + 1);
  }, [articles]);

  useEffect(() => setLoadingState('loading'), []);

  const hasArticles = articlesNodesList.length > 0;

  return (
    <Layout gutter="m">
      {loadingState !== 'done' && !hasArticles && (
        <ListPreloader
          alignContent="center"
          data-testid="article-snippet-list-preloader"
        ></ListPreloader>
      )}
      {hasArticles && (
        <ListWrapper
          currentPage={currentPage}
          flexDirection="column"
          flexWrap="wrap"
          alignContent="space-between"
          gutter={{ 0: 'm', tablet: 'ml', desktop: 'm' }}
          data-testid="article-snippet-list-wrapper"
        >
          {articlesNodesList}
        </ListWrapper>
      )}
      {loadingState === 'done' && !hasArticles && <EmptyList />}
      {lastPage || !hasArticles ? null : (
        <Column size={12} padding={['m', 's', 's']} marginTop={'auto'}>
          <LoadMoreButton
            onClick={() => setLoadingState('loading')}
            disabled={loadingState === 'loading'}
            data-testid="article-snippet-list-load-more"
          >
            {loadingState === 'loading' ? 'Loading...' : 'Load More'}
          </LoadMoreButton>
        </Column>
      )}
    </Layout>
  );
}
